"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import * as RadixForm from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

export const WerForm = () => {
  const [wer, setWer] = useState<number | null>(null);
  const [differences, setDifferences] = useState<Array<WerDifference>>([]);
  const [actualGroundTruth, setActualGroundTruth] = useState("");
  const [actualTestText, setActualTestText] = useState("");
  const formSchema = z.object({
    groundTruth: z.string().min(1),
    testText: z.string().min(1),
    trimText: z.boolean(),
    removePunctuation: z.boolean(),
    ignoreCase: z.boolean(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      groundTruth: "",
      testText: "",
      trimText: true,
      removePunctuation: true,
      ignoreCase: true,
    },
  });

  const handleSubmit = (formData: z.infer<typeof formSchema>) => {
    let groundTruth = formData.groundTruth;
    let testText = formData.testText;
    if (formData.trimText) {
      groundTruth = groundTruth.split(/\s+/).join(" ");
      testText = testText.split(/\s+/).join(" ");
    }
    if (formData.removePunctuation) {
      groundTruth = groundTruth.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
      testText = testText.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "");
    }
    if (formData.ignoreCase) {
      groundTruth = groundTruth.toLowerCase();
      testText = testText.toLowerCase();
    }
    setActualGroundTruth(groundTruth);
    setActualTestText(testText);
    const { wer, differences } = wordErrorRateWithDifferences(groundTruth, testText);
    console.log("differences");
    console.log(differences);
    setWer(wer);
    setDifferences(differences);
  };

  const nInsertions = differences.filter((d) => d.type === "insertion").length;
  const nDeletions = differences.filter((d) => d.type === "deletion").length;
  const nSubstitutions = differences.filter((d) => d.type === "substitution").length;

  return (
    <div>
      <RadixForm.Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="grid grid-cols-2 gap-2">
            <RadixForm.FormField
              control={form.control}
              name="groundTruth"
              render={({ field }) => {
                return (
                  <RadixForm.FormItem>
                    <RadixForm.FormLabel>Ground truth</RadixForm.FormLabel>
                    <RadixForm.FormControl>
                      <Textarea {...field} placeholder="Ground truth" rows={12} />
                    </RadixForm.FormControl>
                    <RadixForm.FormMessage />
                  </RadixForm.FormItem>
                );
              }}
            />
            <RadixForm.FormField
              control={form.control}
              name="testText"
              render={({ field }) => {
                return (
                  <RadixForm.FormItem>
                    <RadixForm.FormLabel>Test text</RadixForm.FormLabel>
                    <RadixForm.FormControl>
                      <Textarea {...field} placeholder="Test text" rows={12} />
                    </RadixForm.FormControl>
                    <RadixForm.FormMessage />
                  </RadixForm.FormItem>
                );
              }}
            />
          </div>
          <RadixForm.FormField
            control={form.control}
            name="removePunctuation"
            render={({ field }) => (
              <RadixForm.FormItem className="flex flex-row items-center justify-start rounded-lg p-2 space-x-4">
                <div className="space-y-0.5">
                  <RadixForm.FormLabel className="text-base">
                    Remove punctuation
                  </RadixForm.FormLabel>
                </div>
                <RadixForm.FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(x) => {
                      console.log("x", x);
                      field.onChange(x);
                      form.handleSubmit(handleSubmit)();
                    }}
                  />
                </RadixForm.FormControl>
              </RadixForm.FormItem>
            )}
          />
          <RadixForm.FormField
            control={form.control}
            name="trimText"
            render={({ field }) => (
              <RadixForm.FormItem className="flex flex-row items-center justify-start rounded-lg p-2 space-x-4">
                <div className="space-y-0.5">
                  <RadixForm.FormLabel className="text-base">Trim text</RadixForm.FormLabel>
                </div>
                <RadixForm.FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(x) => {
                      console.log("x", x);
                      field.onChange(x);
                      form.handleSubmit(handleSubmit)();
                    }}
                  />
                </RadixForm.FormControl>
              </RadixForm.FormItem>
            )}
          />
          <RadixForm.FormField
            control={form.control}
            name="ignoreCase"
            render={({ field }) => (
              <RadixForm.FormItem className="flex flex-row items-center justify-start rounded-lg p-2 space-x-4">
                <div className="space-y-0.5">
                  <RadixForm.FormLabel className="text-base">Ignore case</RadixForm.FormLabel>
                </div>
                <RadixForm.FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(x) => {
                      console.log("x", x);
                      field.onChange(x);
                      form.handleSubmit(handleSubmit)();
                    }}
                  />
                </RadixForm.FormControl>
              </RadixForm.FormItem>
            )}
          />
          <Button type="submit">Submit</Button>
        </form>
      </RadixForm.Form>
      {actualGroundTruth && (
        <div className="mt-8">
          <div className="flex space-x-4 mb-4">
            <div>
              <p>WER</p>
              <p className="text-3xl">{((wer || 0) * 100).toFixed(2)}%</p>
            </div>
            <div>
              <p># differences</p>
              <p className="text-3xl">{differences.length}</p>
            </div>
            <div>
              <p># insertions</p>
              <p className="text-3xl">{nInsertions}</p>
            </div>
            <div>
              <p># deletions</p>
              <p className="text-3xl">{nDeletions}</p>
            </div>
            <div>
              <p># substitutions</p>
              <p className="text-3xl">{nSubstitutions}</p>
            </div>
          </div>
          <WerVisualizer
            reference={actualGroundTruth}
            hypothesis={actualTestText}
            differences={differences}
          />
        </div>
      )}
    </div>
  );
};

type WerDifference = {
  type: "deletion" | "insertion" | "substitution";
  word: string;
  index: number;
};

interface WordErrorRateResult {
  wer: number;
  differences: Array<WerDifference>;
}

function wordErrorRateWithDifferences(reference: string, hypothesis: string): WordErrorRateResult {
  const refWords = reference.trim().split(/\s+/);
  const hypWords = hypothesis.trim().split(/\s+/);
  const R = refWords.length;
  const H = hypWords.length;
  const distanceMatrix = Array.from({ length: R + 1 }, () => Array(H + 1).fill(0));
  const backtrace = Array.from({ length: R + 1 }, () => Array(H + 1).fill(""));

  for (let i = 0; i <= R; i++) {
    distanceMatrix[i][0] = i;
    backtrace[i][0] = "D"; // Deletion
  }

  for (let j = 0; j <= H; j++) {
    distanceMatrix[0][j] = j;
    backtrace[0][j] = "I"; // Insertion
  }

  backtrace[0][0] = "";

  for (let i = 1; i <= R; i++) {
    for (let j = 1; j <= H; j++) {
      const cost = refWords[i - 1] === hypWords[j - 1] ? 0 : 1;
      const del = distanceMatrix[i - 1][j] + 1;
      const ins = distanceMatrix[i][j - 1] + 1;
      const sub = distanceMatrix[i - 1][j - 1] + cost;

      if (del <= ins && del <= sub) {
        distanceMatrix[i][j] = del;
        backtrace[i][j] = "D";
      } else if (ins <= del && ins <= sub) {
        distanceMatrix[i][j] = ins;
        backtrace[i][j] = "I";
      } else {
        distanceMatrix[i][j] = sub;
        backtrace[i][j] = cost === 0 ? "" : "S";
      }
    }
  }

  let i = R;
  let j = H;
  const differences: Array<WerDifference> = [];

  while (i > 0 || j > 0) {
    if (backtrace[i][j] === "D") {
      differences.push({
        type: "deletion",
        word: refWords[i - 1],
        index: i - 1,
      });
      i--;
    } else if (backtrace[i][j] === "I") {
      differences.push({
        type: "insertion",
        word: hypWords[j - 1],
        index: j - 1,
      });
      j--;
    } else if (backtrace[i][j] === "S") {
      differences.push({
        type: "substitution",
        word: hypWords[j - 1],
        index: j - 1,
      });
      i--;
      j--;
    } else {
      i--;
      j--;
    }
  }

  differences.reverse();

  const wer = distanceMatrix[R][H] / R;
  return { wer, differences };
}

interface WerVisualizerProps {
  reference: string;
  hypothesis: string;
  differences: Array<WerDifference>;
}

const WerVisualizer: React.FC<WerVisualizerProps> = ({ reference, hypothesis, differences }) => {
  const renderDifferences = () => {
    if (!differences) return null;

    const highlightedHypothesis = hypothesis.split(/\s+/).map((word, index) => {
      const difference = differences.find((d) => d.index === index && d.type !== "deletion");
      if (difference) {
        if (difference.type === "insertion") {
          return (
            <span key={index} className="bg-success/20">
              {word}{" "}
            </span>
          );
        }
        if (difference.type === "substitution") {
          return (
            <span key={index} className="bg-destructive/20">
              {word}{" "}
            </span>
          );
        }
      }
      return <span key={index}>{word} </span>;
    });

    const highlightedReference = reference.split(/\s+/).map((word, index) => {
      const difference = differences.find((d) => d.index === index && d.type === "deletion");
      if (difference) {
        return (
          <span
            key={index}
            className="bg-info/20"
            style={{
              textDecoration: "line-through",
            }}
          >
            {word}{" "}
          </span>
        );
      }
      return <span key={index}>{word} </span>;
    });

    return (
      <div className="grid grid-cols-2 gap-2">
        <div>
          <h3 className="scroll-m-20 text-xl font-semibold tracking-tight">Ground truth</h3>
          <div>{highlightedReference}</div>
        </div>
        <div>
          <h3 className="scroll-m-20 text-xl font-semibold tracking-tight">Test text</h3>
          <div>{highlightedHypothesis}</div>
        </div>
      </div>
    );
  };

  return <div>{renderDifferences()}</div>;
};
